// /client/src/Guildhall.js

import React, { useState, useEffect } from "react";
import { connectedPeers, updatePeers } from './peers';
import ResponsiveVideoLayout from './ResponsiveVideoLayout';
import { useSocket } from './context/SocketContext';
import { useNavigate } from "react-router-dom";
import roomBackgrounds from "./assets/roomBackgrounds";
import CommandRail from "./components/guildhall/CommandRail";
import GuildhallPanels from "./components/guildhall/GuildhallPanels";

const Guildhall = ({
  localStream,
  localVideoRef,
  remoteStreams,
  isMuted,
  toggleMute,
  toggleCamera,
  isCameraOff,
  cameraOffByPeer,
  isLowBandwidth,
  toggleLowBandwidth,
  hasConnected,
  isReadyToConnect,
  handleConnectClick,
  username,
  roomLeader,
  socketIdToUsername,
  setRemoteStreams,
  roomName,
  userCount,
  socketIdToDisplayName,
  isSessionActive
}) => {
  const { socket } = useSocket(); // ✅ elegant and safe
  const [diceVisible, setDiceVisible] = useState(false);
  //const [userCount, setUserCount] = useState(0);
  const navigate = useNavigate();
  const [isRoomLocked, setIsRoomLocked] = useState(false);
  const backgroundImage = roomBackgrounds[roomName] || roomBackgrounds.Default;
  const [xCardActive, setXCardActive] = useState(false);
  const [initiativeVisible, setInitiativeVisible] = useState(false);
  const [imageVisible, setImageVisible] = useState(false);
  
    useEffect(() => {
  socket.on("x-card-status", ({ active }) => {
    setXCardActive(active);
  });

  return () => {
    socket.off("x-card-status");
  };
}, [socket]);

const handleXCardPress = () => {
    const isLeader = username === roomLeader;

  if (xCardActive && isLeader) {
    socket.emit("x-card-clear", { room: roomName });
  } else if (!xCardActive) {
    socket.emit("x-card-activate", { room: roomName });
  }
};

  useEffect(() => {
  if (!socket) return;

  const handlePeerDisconnected = ({ peerId }) => {
    console.log(`📭 Peer disconnected: ${peerId}`);

    const peerData = connectedPeers.get(peerId);
    if (peerData?.peerConnection?.close) {
      peerData.peerConnection.close();
      console.log(`❌ Closed peer connection for ${peerId}`);
    }
    connectedPeers.delete(peerId);

    if (remoteStreams[peerId]) {
      remoteStreams[peerId].getTracks().forEach(track => track.stop?.());
    }

    if (typeof setRemoteStreams === 'function') {
      setRemoteStreams(prev => {
        const updated = { ...prev };
        delete updated[peerId];
        return updated;
      });
      console.log(`🧹 Removed remote stream for ${peerId}`);
    }
  };
 
  socket.on("peer_disconnected", handlePeerDisconnected);
  
  // ✅ This always runs, even if socket is late
  return () => {
    socket.off("peer_disconnected", handlePeerDisconnected);
  };
}, [socket, remoteStreams, setRemoteStreams]);



useEffect(() => {
  const fetchRoomLockStatus = async () => {
    try {
      const res = await fetch('/api/rooms', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const rooms = await res.json();

      if (res.ok && Array.isArray(rooms)) {
        const currentRoom = rooms.find(r => r.name === roomName);
        if (currentRoom) {
          setIsRoomLocked(currentRoom.locked);
          console.log(`🔒 Room ${roomName} lock status:`, currentRoom.locked);
        } else {
          console.warn("Room not found in room list:", roomName);
        }
      } else {
        console.error("Failed to fetch room list:", rooms.error);
      }
    } catch (err) {
      console.error("Error fetching room list:", err);
    }
  };

  fetchRoomLockStatus();
}, [roomName]);

useEffect(() => {
  // Only auto-connect if a session is already active (i.e., someone clicked Connect before)
  if (isSessionActive && isReadyToConnect && !hasConnected) {
    const timeout = setTimeout(() => {
      handleConnectClick();
    }, 300);
    return () => clearTimeout(timeout);
  }
}, [isSessionActive, isReadyToConnect, hasConnected, handleConnectClick]);


  const handleDisconnect= () => {
    // 1. Stop local media
    if (localStream) {
  localStream.getTracks().forEach(track => track.stop());
  console.log("🛑 Stopped localStream tracks.");
} else if (localVideoRef.current?.srcObject) {
  localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
  console.log("🛑 Fallback: Stopped video element srcObject tracks.");
}

    // 2. Emit manual disconnect and close socket
    if (socket?.connected) {
      socket.emit("manual_disconnect");
      console.log("📡 Sent manual_disconnect and closed socket.");
      socket.disconnect();
    } else {
      console.warn("⚠️ No active socket connection to disconnect.");
    }

    // 3. Targeted cleanup instead of nuking all storage
  localStorage.removeItem("token");
  localStorage.removeItem("username");

    // 4. Navigate back to the Landing Page
    navigate("/");
  };

const handleLockRoom = async () => {
  try {
    const res = await fetch('/api/rooms/lock', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ username, roomName })
    });

    const data = await res.json();
    if (res.ok) {
      setIsRoomLocked(true); // ✅ Update local state
    } else {
      console.error("Lock error:", data.error);
    }
  } catch (err) {
    console.error("Lock failed:", err);
  }
};

  
  const handleUnlockRoom = async () => {
  try {
    const res = await fetch('/api/rooms/unlock', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ username, roomName })
    });

    const data = await res.json();
    if (res.ok) {
      setIsRoomLocked(false); // ✅ Update local state
    } else {
      console.error("Unlock error:", data.error);
    }
  } catch (err) {
    console.error("Unlock failed:", err);
  }
};

 return (
  <div
    style={{
      display: "flex",
      Height: "100vh",
      overflow: "hidden",
      backgroundImage: `url(${backgroundImage})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      width: "100%",
    }}
  >
    <CommandRail
      diceVisible={diceVisible}
      setDiceVisible={setDiceVisible}
      imageVisible={imageVisible}
      setImageVisible={setImageVisible}
      initiativeVisible={initiativeVisible}
      setInitiativeVisible={setInitiativeVisible}
      isRoomLocked={isRoomLocked}
      handleLockRoom={handleLockRoom}
      handleUnlockRoom={handleUnlockRoom}
      xCardActive={xCardActive}
      handleXCardPress={handleXCardPress}
      handleDisconnect={handleDisconnect}
    />

    <GuildhallPanels
      diceVisible={diceVisible}
      imageVisible={imageVisible}
      initiativeVisible={initiativeVisible}
      socket={socket}
      username={username}
      roomName={roomName}
      roomLeader={roomLeader}
    />
    
    {/* 📹 Main video & connect button column */}
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      padding: "20px",
    }}>
      {!hasConnected && (
        <button
          onClick={handleConnectClick}
          disabled={!isReadyToConnect || userCount < 2}
          style={{
            padding: "12px 24px",
            fontSize: "18px",
            fontWeight: "bold",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #4b2e83, #9370db)",
            color: "#fff",
            border: "2px solid #321e57",
            boxShadow: "0 0 12px rgba(75, 46, 131, 0.6)",
            marginBottom: "20px",
            cursor: (isReadyToConnect && userCount >= 2) ? "pointer" : "not-allowed",
            opacity: (isReadyToConnect && userCount >= 2) ? 1 : 0.5,
            transition: "all 0.2s ease-in-out",
          }}
          title="Assemble your adventurers"
        >
          {userCount < 2
            ? "🕯️ The runes remain silent — more must gather."
            : !isReadyToConnect
            ? "🛡️ Preparing the ritual..."
            : "🧙‍♂️ Connect to the Guild"}
        </button>
      )}
      
      <ResponsiveVideoLayout
        localStream={localStream}
        remoteStreams={remoteStreams}
        onMuteClick={toggleMute}
        isCameraOff={isCameraOff}
        onCameraClick={toggleCamera}
        cameraOffByPeer={cameraOffByPeer}
        isMuted={isMuted}
        isLowBandwidth={isLowBandwidth}
        onToggleLBM={toggleLowBandwidth}
        username={username}
        socket={socket}
        roomLeader={roomLeader}
        socketIdToUsername={socketIdToUsername}
        roomName={roomName}
        socketIdToDisplayName={socketIdToDisplayName}
      />
    </div>
  </div>
);

};

export default Guildhall;
